/**
 * MemberIcons — Photo-real skeuomorphic icons for the Member Portal
 *
 * Each 64×64 SVG matches the SECTION_COLORS palette in MemberShell.
 * References shared defs from SharedSvgDefs (pr-*) plus member-specific
 * inline gradients for section-unique tints.
 *
 * forwardRef<SVGSVGElement, IconProps> pattern for React compatibility.
 */

import { forwardRef, type ComponentPropsWithoutRef } from "react";

type IconProps = ComponentPropsWithoutRef<"svg">;

/* ─────────────────────────────────────────────────────────────
 *  1. MRHome — Warm cosy house with chimney & glowing window
 * ───────────────────────────────────────────────────────────── */
export const MRHome = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-home-wall" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#d4a574" />
        <stop offset="100%" stopColor="#a0845c" />
      </linearGradient>
      <linearGradient id="mr-home-roof" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#b8651a" />
        <stop offset="100%" stopColor="#8a4c12" />
      </linearGradient>
    </defs>
    {/* Shadow */}
    <ellipse cx="32" cy="56" rx="20" ry="3" fill="#000" opacity={0.1} />
    {/* Wall */}
    <rect x="16" y="30" width="32" height="22" rx="2" fill="url(#mr-home-wall)" />
    {/* Roof */}
    <path d="M10 32 L32 14 L54 32 Z" fill="url(#mr-home-roof)" />
    <path d="M10 32 L32 14 L54 32" stroke="#6d3a1a" strokeWidth="1.5" fill="none" />
    {/* Door */}
    <rect x="27" y="38" width="10" height="14" rx="1" fill="#5c3d1e" />
    <circle cx="35" cy="46" r="1" fill="#facc15" />
    {/* Window */}
    <rect x="19" y="34" width="6" height="6" rx="1" fill="#fde68a" opacity={0.9} />
    <line x1="22" y1="34" x2="22" y2="40" stroke="#a0845c" strokeWidth="0.5" />
    <line x1="19" y1="37" x2="25" y2="37" stroke="#a0845c" strokeWidth="0.5" />
    {/* Chimney */}
    <rect x="40" y="16" width="6" height="12" rx="1" fill="#94a3b8" />
    <rect x="39" y="15" width="8" height="2" rx="0.5" fill="#64748b" />
    {/* Window glow */}
    <rect x="19" y="34" width="6" height="6" rx="1" fill="#fde68a" opacity={0.3} />
  </svg>
));
MRHome.displayName = "MRHome";

/* ─────────────────────────────────────────────────────────────
 *  2. MRAnnouncements — Notice board with pinned notes
 * ───────────────────────────────────────────────────────────── */
export const MRAnnouncements = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-board" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#d4a574" />
        <stop offset="100%" stopColor="#92704a" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="18" ry="3" fill="#000" opacity={0.1} />
    {/* Cork board */}
    <rect x="10" y="10" width="44" height="40" rx="3" fill="url(#mr-board)" />
    <rect x="10" y="10" width="44" height="40" rx="3" stroke="#78603c" strokeWidth="1.5" />
    {/* Frame */}
    <rect x="8" y="8" width="48" height="44" rx="4" stroke="#5c3d1e" strokeWidth="2" fill="none" />
    {/* Note 1 — white */}
    <rect x="14" y="16" width="14" height="12" rx="1" fill="#fefce8" transform="rotate(-3 14 16)" />
    {/* Note 2 — blue */}
    <rect x="32" y="14" width="14" height="12" rx="1" fill="#bfdbfe" transform="rotate(2 32 14)" />
    {/* Note 3 — pink */}
    <rect x="16" y="32" width="12" height="10" rx="1" fill="#fecdd3" transform="rotate(1 16 32)" />
    {/* Note 4 — green */}
    <rect x="34" y="30" width="14" height="12" rx="1" fill="#bbf7d0" transform="rotate(-2 34 30)" />
    {/* Pins */}
    <circle cx="20" cy="17" r="2" fill="#ef4444" />
    <circle cx="38" cy="15" r="2" fill="#3b82f6" />
    <circle cx="22" cy="33" r="2" fill="#f59e0b" />
    <circle cx="40" cy="31" r="2" fill="#22c55e" />
    {/* Pin highlights */}
    <circle cx="19" cy="16" r="0.8" fill="#fff" opacity={0.6} />
    <circle cx="37" cy="14" r="0.8" fill="#fff" opacity={0.6} />
  </svg>
));
MRAnnouncements.displayName = "MRAnnouncements";

/* ─────────────────────────────────────────────────────────────
 *  3. MRProfile — Portrait frame with silhouette
 * ───────────────────────────────────────────────────────────── */
export const MRProfile = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-frame-blue" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#60a5fa" />
        <stop offset="100%" stopColor="#2563eb" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="16" ry="3" fill="#000" opacity={0.1} />
    {/* Outer frame */}
    <rect x="12" y="8" width="40" height="46" rx="4" fill="url(#mr-frame-blue)" />
    {/* Inner mat */}
    <rect x="16" y="12" width="32" height="38" rx="2" fill="#f8fafc" />
    {/* Head */}
    <circle cx="32" cy="28" r="8" fill="#cbd5e1" />
    {/* Body silhouette */}
    <ellipse cx="32" cy="48" rx="12" ry="8" fill="#cbd5e1" />
    {/* Frame shine */}
    <rect x="12" y="8" width="40" height="4" rx="2" fill="#93c5fd" opacity={0.5} />
    {/* Frame corners decorative */}
    <circle cx="16" cy="12" r="1.5" fill="#93c5fd" opacity={0.4} />
    <circle cx="48" cy="12" r="1.5" fill="#93c5fd" opacity={0.4} />
  </svg>
));
MRProfile.displayName = "MRProfile";

/* ─────────────────────────────────────────────────────────────
 *  4. MRHousehold — Family group with house outline
 * ───────────────────────────────────────────────────────────── */
export const MRHousehold = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-purple-soft" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#c4b5fd" />
        <stop offset="100%" stopColor="#8b5cf6" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="18" ry="3" fill="#000" opacity={0.1} />
    {/* House outline */}
    <path d="M10 34 L32 16 L54 34 V52 H10 Z" fill="url(#mr-purple-soft)" opacity={0.15} stroke="#8b5cf6" strokeWidth="1.5" />
    {/* Adult 1 */}
    <circle cx="24" cy="32" r="5" fill="#a78bfa" />
    <ellipse cx="24" cy="44" rx="6" ry="5" fill="#a78bfa" />
    {/* Adult 2 */}
    <circle cx="40" cy="32" r="5" fill="#7c3aed" />
    <ellipse cx="40" cy="44" rx="6" ry="5" fill="#7c3aed" />
    {/* Child */}
    <circle cx="32" cy="38" r="3.5" fill="#c4b5fd" />
    <ellipse cx="32" cy="47" rx="4" ry="3.5" fill="#c4b5fd" />
    {/* Heart */}
    <path d="M30 25 C30 23 32 21 32 23 C32 21 34 23 34 25 L32 28 Z" fill="#f472b6" />
  </svg>
));
MRHousehold.displayName = "MRHousehold";

/* ─────────────────────────────────────────────────────────────
 *  5. MRDirectory — Address book with tabs
 * ───────────────────────────────────────────────────────────── */
export const MRDirectory = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-teal-book" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#5eead4" />
        <stop offset="100%" stopColor="#0d9488" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="16" ry="3" fill="#000" opacity={0.1} />
    {/* Book body */}
    <rect x="14" y="8" width="32" height="44" rx="3" fill="url(#mr-teal-book)" />
    {/* Spine */}
    <rect x="14" y="8" width="5" height="44" rx="2" fill="#0d9488" />
    {/* Pages */}
    <rect x="20" y="11" width="24" height="38" rx="2" fill="#f0fdfa" />
    {/* A-Z tabs on right */}
    <rect x="44" y="14" width="8" height="6" rx="1" fill="#14b8a6" />
    <rect x="44" y="24" width="8" height="6" rx="1" fill="#0d9488" />
    <rect x="44" y="34" width="8" height="6" rx="1" fill="#0f766e" />
    {/* Tab labels */}
    <text x="48" y="19" textAnchor="middle" fontSize="4" fontWeight="bold" fill="#fff">A-F</text>
    <text x="48" y="29" textAnchor="middle" fontSize="4" fontWeight="bold" fill="#fff">G-M</text>
    <text x="48" y="39" textAnchor="middle" fontSize="4" fontWeight="bold" fill="#fff">N-Z</text>
    {/* Contact lines */}
    <line x1="24" y1="18" x2="40" y2="18" stroke="#99f6e4" strokeWidth="1.5" />
    <line x1="24" y1="22" x2="36" y2="22" stroke="#99f6e4" strokeWidth="1" />
    <line x1="24" y1="28" x2="40" y2="28" stroke="#99f6e4" strokeWidth="1.5" />
    <line x1="24" y1="32" x2="36" y2="32" stroke="#99f6e4" strokeWidth="1" />
    <line x1="24" y1="38" x2="40" y2="38" stroke="#99f6e4" strokeWidth="1.5" />
    <line x1="24" y1="42" x2="36" y2="42" stroke="#99f6e4" strokeWidth="1" />
  </svg>
));
MRDirectory.displayName = "MRDirectory";

/* ─────────────────────────────────────────────────────────────
 *  6. MRGiving — Open hands with gold coin
 * ───────────────────────────────────────────────────────────── */
export const MRGiving = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <ellipse cx="32" cy="56" rx="16" ry="3" fill="#000" opacity={0.1} />
    {/* Hands cupped shape */}
    <path d="M14 36 C14 28 22 22 32 22 C42 22 50 28 50 36 C50 42 42 46 32 46 C22 46 14 42 14 36Z" fill="#86efac" opacity={0.3} />
    <path d="M12 38 C10 34 14 26 22 24 L18 40 Z" fill="#4ade80" />
    <path d="M52 38 C54 34 50 26 42 24 L46 40 Z" fill="#4ade80" />
    {/* Coin */}
    <ellipse cx="32" cy="30" rx="10" ry="9" fill="url(#pr-gold-face)" />
    <ellipse cx="32" cy="30" rx="10" ry="9" fill="url(#pr-gold-shine)" />
    <ellipse cx="32" cy="32" rx="10" ry="2" fill="url(#pr-gold-rim)" />
    {/* Dollar sign */}
    <text x="32" y="34" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#a16207">$</text>
    {/* Sparkles */}
    <circle cx="18" cy="20" r="1.5" fill="#facc15" opacity={0.7} />
    <circle cx="46" cy="18" r="1" fill="#facc15" opacity={0.5} />
    <circle cx="24" cy="14" r="1" fill="#fde047" opacity={0.6} />
  </svg>
));
MRGiving.displayName = "MRGiving";

/* ─────────────────────────────────────────────────────────────
 *  7. MRGroups — People in a circle
 * ───────────────────────────────────────────────────────────── */
export const MRGroups = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-cyan" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#67e8f9" />
        <stop offset="100%" stopColor="#0891b2" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="18" ry="3" fill="#000" opacity={0.1} />
    {/* Circle connection */}
    <circle cx="32" cy="32" r="18" stroke="#0891b2" strokeWidth="1" strokeDasharray="3 2" fill="none" opacity={0.3} />
    {/* Person top */}
    <circle cx="32" cy="14" r="5" fill="#22d3ee" />
    <ellipse cx="32" cy="22" rx="5" ry="3" fill="#22d3ee" />
    {/* Person left */}
    <circle cx="16" cy="36" r="5" fill="#06b6d4" />
    <ellipse cx="16" cy="44" rx="5" ry="3" fill="#06b6d4" />
    {/* Person right */}
    <circle cx="48" cy="36" r="5" fill="#0891b2" />
    <ellipse cx="48" cy="44" rx="5" ry="3" fill="#0891b2" />
    {/* Person bottom */}
    <circle cx="32" cy="46" r="4" fill="#67e8f9" />
    <ellipse cx="32" cy="52" rx="4" ry="2.5" fill="#67e8f9" />
    {/* Connection lines */}
    <line x1="27" y1="18" x2="20" y2="32" stroke="#0891b2" strokeWidth="0.8" opacity={0.4} />
    <line x1="37" y1="18" x2="44" y2="32" stroke="#0891b2" strokeWidth="0.8" opacity={0.4} />
    <line x1="21" y1="40" x2="28" y2="44" stroke="#0891b2" strokeWidth="0.8" opacity={0.4} />
    <line x1="43" y1="40" x2="36" y2="44" stroke="#0891b2" strokeWidth="0.8" opacity={0.4} />
  </svg>
));
MRGroups.displayName = "MRGroups";

/* ─────────────────────────────────────────────────────────────
 *  8. MRMessages — Sealed envelope with wax seal
 * ───────────────────────────────────────────────────────────── */
export const MRMessages = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <ellipse cx="32" cy="56" rx="18" ry="3" fill="#000" opacity={0.1} />
    {/* Envelope body */}
    <rect x="8" y="18" width="48" height="30" rx="3" fill="url(#pr-paper-white)" />
    <rect x="8" y="18" width="48" height="30" rx="3" stroke="#cbd5e1" strokeWidth="1" />
    {/* Envelope flap */}
    <path d="M8 20 L32 36 L56 20" fill="#e2e8f0" />
    <path d="M8 20 L32 36 L56 20" stroke="#94a3b8" strokeWidth="1" fill="none" />
    {/* Wax seal */}
    <circle cx="32" cy="42" r="6" fill="url(#pr-wax-red)" />
    <circle cx="32" cy="42" r="4" fill="#dc2626" />
    <circle cx="30" cy="40" r="1.5" fill="#fca5a5" opacity={0.5} />
    {/* Seal emboss */}
    <text x="32" y="44.5" textAnchor="middle" fontSize="5" fontWeight="bold" fill="#fecaca" opacity={0.7}>✉</text>
  </svg>
));
MRMessages.displayName = "MRMessages";

/* ─────────────────────────────────────────────────────────────
 *  9. MREvents — Calendar with celebration confetti
 * ───────────────────────────────────────────────────────────── */
export const MREvents = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-cal-teal" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#14b8a6" />
        <stop offset="100%" stopColor="#0f766e" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="16" ry="3" fill="#000" opacity={0.1} />
    {/* Calendar body */}
    <rect x="12" y="14" width="40" height="38" rx="4" fill="#f0fdfa" />
    <rect x="12" y="14" width="40" height="38" rx="4" stroke="#99f6e4" strokeWidth="1" />
    {/* Header bar */}
    <rect x="12" y="14" width="40" height="10" rx="4" fill="url(#mr-cal-teal)" />
    {/* Calendar rings */}
    <rect x="22" y="10" width="3" height="8" rx="1.5" fill="#0d9488" />
    <rect x="39" y="10" width="3" height="8" rx="1.5" fill="#0d9488" />
    {/* Date grid */}
    <circle cx="22" cy="32" r="2" fill="#d1fae5" />
    <circle cx="32" cy="32" r="2" fill="#d1fae5" />
    <circle cx="42" cy="32" r="2" fill="#d1fae5" />
    <circle cx="22" cy="40" r="2" fill="#d1fae5" />
    <circle cx="32" cy="40" r="3" fill="#14b8a6" />
    <circle cx="42" cy="40" r="2" fill="#d1fae5" />
    {/* Star / event marker */}
    <text x="32" y="42" textAnchor="middle" fontSize="5" fill="#fff">★</text>
    {/* Confetti */}
    <rect x="10" y="6" width="3" height="1.5" rx="0.5" fill="#f472b6" transform="rotate(-20 10 6)" />
    <rect x="50" y="8" width="3" height="1.5" rx="0.5" fill="#facc15" transform="rotate(15 50 8)" />
    <circle cx="46" cy="5" r="1.5" fill="#a78bfa" />
    <circle cx="16" cy="9" r="1" fill="#22d3ee" />
  </svg>
));
MREvents.displayName = "MREvents";

/* ─────────────────────────────────────────────────────────────
 *  10. MRRegistrations — Event ticket stub
 * ───────────────────────────────────────────────────────────── */
export const MRRegistrations = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-ticket-orange" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fdba74" />
        <stop offset="100%" stopColor="#ea580c" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="16" ry="3" fill="#000" opacity={0.1} />
    {/* Ticket body */}
    <path d="M10 16 H54 V28 C50 28 50 34 54 34 V48 H10 V34 C14 34 14 28 10 28 Z" fill="url(#mr-ticket-orange)" rx="3" />
    {/* Perforation line */}
    <line x1="10" y1="31" x2="54" y2="31" stroke="#fff" strokeWidth="0.8" strokeDasharray="3 2" opacity={0.5} />
    {/* Ticket text area */}
    <rect x="16" y="20" width="20" height="2" rx="1" fill="#fff" opacity={0.7} />
    <rect x="16" y="24" width="14" height="1.5" rx="0.75" fill="#fff" opacity={0.4} />
    {/* Barcode */}
    <rect x="16" y="36" width="2" height="8" fill="#fff" opacity={0.6} />
    <rect x="20" y="36" width="1" height="8" fill="#fff" opacity={0.6} />
    <rect x="23" y="36" width="2" height="8" fill="#fff" opacity={0.6} />
    <rect x="27" y="36" width="1.5" height="8" fill="#fff" opacity={0.6} />
    <rect x="30" y="36" width="2" height="8" fill="#fff" opacity={0.6} />
    <rect x="34" y="36" width="1" height="8" fill="#fff" opacity={0.6} />
    <rect x="37" y="36" width="2" height="8" fill="#fff" opacity={0.6} />
    <rect x="41" y="36" width="1.5" height="8" fill="#fff" opacity={0.6} />
    {/* Check mark */}
    <circle cx="44" cy="22" r="5" fill="#fff" opacity={0.3} />
    <path d="M41 22 L43 24 L47 20" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
));
MRRegistrations.displayName = "MRRegistrations";

/* ─────────────────────────────────────────────────────────────
 *  11. MRAttendance — Calendar with check mark
 * ───────────────────────────────────────────────────────────── */
export const MRAttendance = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-cal-blue" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#60a5fa" />
        <stop offset="100%" stopColor="#2563eb" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="16" ry="3" fill="#000" opacity={0.1} />
    {/* Calendar body */}
    <rect x="12" y="14" width="40" height="38" rx="4" fill="#eff6ff" />
    <rect x="12" y="14" width="40" height="38" rx="4" stroke="#bfdbfe" strokeWidth="1" />
    {/* Header */}
    <rect x="12" y="14" width="40" height="10" rx="4" fill="url(#mr-cal-blue)" />
    {/* Rings */}
    <rect x="22" y="10" width="3" height="8" rx="1.5" fill="#1d4ed8" />
    <rect x="39" y="10" width="3" height="8" rx="1.5" fill="#1d4ed8" />
    {/* Big checkmark */}
    <circle cx="32" cy="38" r="10" fill="#dbeafe" />
    <path d="M26 38 L30 42 L38 34" stroke="#2563eb" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
));
MRAttendance.displayName = "MRAttendance";

/* ─────────────────────────────────────────────────────────────
 *  12. MRPledges — Handshake with green badge
 * ───────────────────────────────────────────────────────────── */
export const MRPledges = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-green-hand" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#86efac" />
        <stop offset="100%" stopColor="#16a34a" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="18" ry="3" fill="#000" opacity={0.1} />
    {/* Left hand */}
    <path d="M10 30 C10 24 18 20 24 24 L32 32 L24 36 C16 38 10 36 10 30Z" fill="#4ade80" />
    {/* Right hand */}
    <path d="M54 30 C54 24 46 20 40 24 L32 32 L40 36 C48 38 54 36 54 30Z" fill="#22c55e" />
    {/* Handshake clasp */}
    <ellipse cx="32" cy="32" rx="6" ry="4" fill="#16a34a" />
    {/* Green badge */}
    <circle cx="32" cy="16" r="8" fill="url(#mr-green-hand)" />
    <circle cx="32" cy="16" r="6" fill="#15803d" />
    <path d="M29 16 L31 18 L35 14" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    {/* Dollar sparkle */}
    <text x="18" y="22" fontSize="6" fill="#facc15" opacity={0.7}>✦</text>
    <text x="44" y="22" fontSize="5" fill="#facc15" opacity={0.5}>✦</text>
  </svg>
));
MRPledges.displayName = "MRPledges";

/* ─────────────────────────────────────────────────────────────
 *  13. MRRequests — Clipboard with highlight pen
 * ───────────────────────────────────────────────────────────── */
export const MRRequests = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-gold-clip" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fde68a" />
        <stop offset="100%" stopColor="#d49b0a" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="14" ry="3" fill="#000" opacity={0.1} />
    {/* Board */}
    <rect x="14" y="12" width="36" height="42" rx="3" fill="#fef9c3" />
    <rect x="14" y="12" width="36" height="42" rx="3" stroke="#d49b0a" strokeWidth="1" />
    {/* Clip */}
    <rect x="24" y="8" width="16" height="8" rx="3" fill="url(#mr-gold-clip)" />
    <rect x="28" y="10" width="8" height="4" rx="1.5" fill="#fefce8" />
    {/* Lines */}
    <rect x="20" y="22" width="18" height="2" rx="1" fill="#fbbf24" opacity={0.5} />
    <rect x="20" y="28" width="22" height="2" rx="1" fill="#fbbf24" opacity={0.5} />
    <rect x="20" y="34" width="16" height="2" rx="1" fill="#fbbf24" opacity={0.5} />
    <rect x="20" y="40" width="20" height="2" rx="1" fill="#fbbf24" opacity={0.5} />
    {/* Checkboxes */}
    <rect x="20" y="22" width="2" height="2" rx="0.3" fill="#d49b0a" />
    <rect x="20" y="28" width="2" height="2" rx="0.3" fill="#d49b0a" />
    <rect x="20" y="34" width="2" height="2" rx="0.3" fill="#d49b0a" />
    {/* Pen */}
    <rect x="40" y="30" width="3" height="18" rx="1" fill="#b45309" transform="rotate(20 40 30)" />
    <polygon points="42,48 41,52 44,48" fill="#1e293b" transform="rotate(20 40 30)" />
  </svg>
));
MRRequests.displayName = "MRRequests";

/* ─────────────────────────────────────────────────────────────
 *  14. MRPrayer — Candle with warm glow
 * ───────────────────────────────────────────────────────────── */
export const MRPrayer = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <radialGradient id="mr-candle-glow" cx="0.5" cy="0.4" r="0.5">
        <stop offset="0%" stopColor="#fde68a" stopOpacity={0.6} />
        <stop offset="100%" stopColor="#fde68a" stopOpacity={0} />
      </radialGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="12" ry="3" fill="#000" opacity={0.1} />
    {/* Glow */}
    <circle cx="32" cy="20" r="16" fill="url(#mr-candle-glow)" />
    {/* Candle body */}
    <rect x="27" y="28" width="10" height="22" rx="2" fill="#f5f5f4" />
    <rect x="27" y="28" width="10" height="22" rx="2" stroke="#d6d3d1" strokeWidth="0.5" />
    {/* Candle base holder */}
    <ellipse cx="32" cy="50" rx="10" ry="3" fill="#a78bfa" />
    <ellipse cx="32" cy="49" rx="10" ry="3" fill="#c4b5fd" />
    {/* Wick */}
    <line x1="32" y1="28" x2="32" y2="22" stroke="#57534e" strokeWidth="1" />
    {/* Flame */}
    <ellipse cx="32" cy="18" rx="4" ry="7" fill="#fbbf24" />
    <ellipse cx="32" cy="17" rx="2.5" ry="5" fill="#fde68a" />
    <ellipse cx="32" cy="16" rx="1.2" ry="3" fill="#fff" opacity={0.7} />
    {/* Dripping wax */}
    <circle cx="29" cy="32" r="1" fill="#e7e5e4" />
    <circle cx="35" cy="36" r="0.8" fill="#e7e5e4" />
  </svg>
));
MRPrayer.displayName = "MRPrayer";

/* ─────────────────────────────────────────────────────────────
 *  15. MRMedia — Film reel / play button
 * ───────────────────────────────────────────────────────────── */
export const MRMedia = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-media-orange" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fdba74" />
        <stop offset="100%" stopColor="#c2410c" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="18" ry="3" fill="#000" opacity={0.1} />
    {/* Film reel body */}
    <circle cx="32" cy="30" r="20" fill="url(#mr-media-orange)" />
    <circle cx="32" cy="30" r="18" fill="#1e293b" />
    {/* Sprocket holes */}
    <circle cx="32" cy="14" r="3" fill="#334155" />
    <circle cx="32" cy="46" r="3" fill="#334155" />
    <circle cx="16" cy="30" r="3" fill="#334155" />
    <circle cx="48" cy="30" r="3" fill="#334155" />
    <circle cx="20" cy="18" r="2.5" fill="#334155" />
    <circle cx="44" cy="18" r="2.5" fill="#334155" />
    <circle cx="20" cy="42" r="2.5" fill="#334155" />
    <circle cx="44" cy="42" r="2.5" fill="#334155" />
    {/* Center hub */}
    <circle cx="32" cy="30" r="6" fill="#475569" />
    <circle cx="32" cy="30" r="3" fill="#64748b" />
    {/* Play triangle overlay */}
    <polygon points="28,24 28,36 40,30" fill="#fff" opacity={0.9} />
  </svg>
));
MRMedia.displayName = "MRMedia";

/* ─────────────────────────────────────────────────────────────
 *  16. MRNotifications — Golden bell with red badge
 * ───────────────────────────────────────────────────────────── */
export const MRNotifications = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <ellipse cx="32" cy="56" rx="14" ry="3" fill="#000" opacity={0.1} />
    {/* Bell body */}
    <path d="M20 34 C20 22 24 14 32 14 C40 14 44 22 44 34 L46 38 H18 L20 34Z" fill="url(#pr-gold-face)" />
    <path d="M20 34 C20 22 24 14 32 14 C40 14 44 22 44 34 L46 38 H18 L20 34Z" fill="url(#pr-gold-shine)" />
    {/* Bell rim */}
    <rect x="16" y="38" width="32" height="4" rx="2" fill="url(#pr-gold-rim)" />
    {/* Clapper */}
    <circle cx="32" cy="46" r="3" fill="#ca8a04" />
    {/* Handle */}
    <path d="M30 14 C30 10 34 10 34 14" stroke="#a16207" strokeWidth="2" fill="none" />
    {/* Red notification badge */}
    <circle cx="44" cy="16" r="6" fill="#ef4444" />
    <text x="44" y="19" textAnchor="middle" fontSize="8" fontWeight="bold" fill="#fff">3</text>
    {/* Shine */}
    <ellipse cx="28" cy="22" rx="3" ry="5" fill="#fff" opacity={0.15} />
  </svg>
));
MRNotifications.displayName = "MRNotifications";

/* ─────────────────────────────────────────────────────────────
 *  17. MRSchedule — Time blocks / weekly planner
 * ───────────────────────────────────────────────────────────── */
export const MRSchedule = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-sched-teal" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#5eead4" />
        <stop offset="100%" stopColor="#0d9488" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="16" ry="3" fill="#000" opacity={0.1} />
    {/* Planner body */}
    <rect x="10" y="10" width="44" height="42" rx="4" fill="#f0fdfa" />
    <rect x="10" y="10" width="44" height="42" rx="4" stroke="#99f6e4" strokeWidth="1" />
    {/* Header */}
    <rect x="10" y="10" width="44" height="8" rx="4" fill="url(#mr-sched-teal)" />
    {/* Time blocks */}
    <rect x="14" y="22" width="12" height="6" rx="2" fill="#99f6e4" />
    <rect x="14" y="30" width="18" height="6" rx="2" fill="#5eead4" />
    <rect x="14" y="38" width="10" height="6" rx="2" fill="#2dd4bf" />
    <rect x="36" y="22" width="14" height="6" rx="2" fill="#14b8a6" opacity={0.3} />
    <rect x="36" y="30" width="8" height="6" rx="2" fill="#14b8a6" opacity={0.3} />
    <rect x="28" y="38" width="22" height="6" rx="2" fill="#0d9488" opacity={0.2} />
    {/* Clock icon in header */}
    <circle cx="32" cy="14" r="3" fill="#fff" opacity={0.3} />
    <line x1="32" y1="12" x2="32" y2="14" stroke="#fff" strokeWidth="0.8" />
    <line x1="32" y1="14" x2="34" y2="14" stroke="#fff" strokeWidth="0.8" />
  </svg>
));
MRSchedule.displayName = "MRSchedule";

/* ─────────────────────────────────────────────────────────────
 *  18. MRAvailability — Raised hand
 * ───────────────────────────────────────────────────────────── */
export const MRAvailability = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-avail-purple" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#c4b5fd" />
        <stop offset="100%" stopColor="#7c3aed" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="14" ry="3" fill="#000" opacity={0.1} />
    {/* Palm */}
    <path d="M22 32 C22 24 26 18 32 18 C38 18 42 24 42 32 V42 C42 46 38 50 32 50 C26 50 22 46 22 42 Z" fill="url(#mr-avail-purple)" />
    {/* Fingers */}
    <rect x="23" y="10" width="4" height="16" rx="2" fill="#a78bfa" />
    <rect x="29" y="8" width="4" height="18" rx="2" fill="#a78bfa" />
    <rect x="35" y="10" width="4" height="16" rx="2" fill="#a78bfa" />
    <rect x="41" y="14" width="4" height="12" rx="2" fill="#a78bfa" />
    {/* Thumb */}
    <rect x="17" y="24" width="4" height="10" rx="2" fill="#a78bfa" transform="rotate(-20 17 24)" />
    {/* Check badge */}
    <circle cx="44" cy="42" r="6" fill="#22c55e" />
    <path d="M41 42 L43 44 L47 40" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
));
MRAvailability.displayName = "MRAvailability";

/* ─────────────────────────────────────────────────────────────
 *  19. MRPreferences — Settings gear with warm tones
 * ───────────────────────────────────────────────────────────── */
export const MRPreferences = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-gear-warm" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#fde68a" />
        <stop offset="100%" stopColor="#92400e" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="14" ry="3" fill="#000" opacity={0.1} />
    {/* Gear teeth */}
    <path d="M29 8 H35 L36 13 L40 14 L44 10 L48 14 L45 18 L46 22 H52 V28 H46 L45 32 L48 36 L44 40 L40 36 L36 37 L35 42 H29 L28 37 L24 36 L20 40 L16 36 L19 32 L18 28 H12 V22 H18 L19 18 L16 14 L20 10 L24 14 L28 13 Z" fill="url(#mr-gear-warm)" />
    {/* Inner circle */}
    <circle cx="32" cy="25" r="8" fill="#78350f" />
    <circle cx="32" cy="25" r="5" fill="#92400e" />
    {/* Shine */}
    <circle cx="28" cy="21" r="3" fill="#fde68a" opacity={0.2} />
    {/* Toggle switches below */}
    <rect x="20" y="46" width="10" height="4" rx="2" fill="#d6d3d1" />
    <circle cx="27" cy="48" r="2.5" fill="#22c55e" />
    <rect x="34" y="46" width="10" height="4" rx="2" fill="#d6d3d1" />
    <circle cx="37" cy="48" r="2.5" fill="#ef4444" />
  </svg>
));
MRPreferences.displayName = "MRPreferences";

/* ─────────────────────────────────────────────────────────────
 *  20. MRAdmin (Back to Admin) — door with arrow
 * ───────────────────────────────────────────────────────────── */
export const MRAdmin = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <ellipse cx="32" cy="56" rx="14" ry="3" fill="#000" opacity={0.1} />
    {/* Door frame */}
    <rect x="18" y="10" width="28" height="42" rx="3" fill="url(#pr-leather)" />
    <rect x="18" y="10" width="28" height="42" rx="3" stroke="#5c3d1e" strokeWidth="1.5" />
    {/* Door panel */}
    <rect x="22" y="14" width="20" height="34" rx="2" fill="url(#pr-leather-dark)" />
    {/* Door handle */}
    <circle cx="38" cy="32" r="2" fill="url(#pr-gold-face)" />
    <circle cx="38" cy="32" r="1" fill="#fde047" />
    {/* Arrow pointing out */}
    <path d="M8 32 L16 32 M12 28 L8 32 L12 36" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
));
MRAdmin.displayName = "MRAdmin";

/* ─────────────────────────────────────────────────────────────
 *  Volunteer-specific icons
 * ───────────────────────────────────────────────────────────── */

/* 21. MRTasks — Clipboard with check marks */
export const MRTasks = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <defs>
      <linearGradient id="mr-task-green" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#86efac" />
        <stop offset="100%" stopColor="#16a34a" />
      </linearGradient>
    </defs>
    <ellipse cx="32" cy="56" rx="14" ry="3" fill="#000" opacity={0.1} />
    {/* Board */}
    <rect x="14" y="12" width="36" height="42" rx="3" fill="#f0fdf4" />
    <rect x="14" y="12" width="36" height="42" rx="3" stroke="#86efac" strokeWidth="1" />
    {/* Clip */}
    <rect x="24" y="8" width="16" height="8" rx="3" fill="url(#mr-task-green)" />
    <rect x="28" y="10" width="8" height="4" rx="1.5" fill="#dcfce7" />
    {/* Task rows with checks */}
    <rect x="20" y="24" width="4" height="4" rx="1" fill="#22c55e" />
    <path d="M21 26 L22 27 L23.5 25" stroke="#fff" strokeWidth="0.8" strokeLinecap="round" />
    <rect x="28" y="24" width="16" height="2" rx="1" fill="#bbf7d0" />

    <rect x="20" y="32" width="4" height="4" rx="1" fill="#22c55e" />
    <path d="M21 34 L22 35 L23.5 33" stroke="#fff" strokeWidth="0.8" strokeLinecap="round" />
    <rect x="28" y="32" width="12" height="2" rx="1" fill="#bbf7d0" />

    <rect x="20" y="40" width="4" height="4" rx="1" fill="#d1d5db" />
    <rect x="28" y="40" width="18" height="2" rx="1" fill="#e5e7eb" />
  </svg>
));
MRTasks.displayName = "MRTasks";

/* 22. MRLeader — Crown / shield */
export const MRLeader = forwardRef<SVGSVGElement, IconProps>(({ className, ...props }, ref) => (
  <svg ref={ref} viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg" className={className} {...props}>
    <ellipse cx="32" cy="56" rx="14" ry="3" fill="#000" opacity={0.1} />
    {/* Shield */}
    <path d="M14 16 H50 V36 C50 46 42 52 32 54 C22 52 14 46 14 36 Z" fill="url(#pr-glass-blue)" />
    <path d="M14 16 H50 V36 C50 46 42 52 32 54 C22 52 14 46 14 36 Z" fill="url(#pr-glass-shine)" />
    <path d="M14 16 H50 V36 C50 46 42 52 32 54 C22 52 14 46 14 36 Z" stroke="#1d4ed8" strokeWidth="1.5" fill="none" />
    {/* Star */}
    <polygon points="32,22 35,30 43,30 37,35 39,43 32,38 25,43 27,35 21,30 29,30" fill="url(#pr-gold-face)" />
    <polygon points="32,24 34,29 40,29 35,33 37,40 32,36 27,40 29,33 24,29 30,29" fill="#fde047" opacity={0.6} />
  </svg>
));
MRLeader.displayName = "MRLeader";
